import React, { useState, useRef } from 'react';
import { Upload, Trash2, FileText } from 'lucide-react';
import api from '../../api/client';
import toast from 'react-hot-toast';

interface Props {
  bookId: number;
  filePath?: string;
  fileType?: string;
  onUpdate: () => void;
}

export default function FileUpload({ bookId, filePath, fileType, onUpdate }: Props) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File) => {
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!['pdf','epub'].includes(ext||'')) return toast.error('მხოლოდ PDF ან EPUB');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/upload/book/${bookId}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('ფაილი ატვირთულია!');
      onUpdate();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'ატვირთვა ვერ მოხდა');
    } finally { setUploading(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm('ფაილი წაიშლება?')) return;
    try {
      await api.delete(`/upload/book/${bookId}`);
      toast.success('ფაილი წაიშალა');
      onUpdate();
    } catch { toast.error('შეცდომა'); }
  };

  if (filePath && filePath !== 'none') {
    return (
      <div style={{display:'flex',flexDirection:'column',gap:12}}>
        <div style={{display:'flex',alignItems:'center',gap:12,padding:16,background:'var(--bg-input)',border:'1px solid #1e2d45',borderRadius:10}}>
          <div style={{width:44,height:44,borderRadius:10,background:'#1a0d2e',border:'1px solid #3a1d6e',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
            <FileText size={22} style={{color:'#a78bfa'}} />
          </div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:13,color:'var(--text-secondary)',fontWeight:500,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{filePath}</div>
            <div style={{fontSize:11,color:'var(--text-faint)',marginTop:2}}>{fileType?.toUpperCase()} ფაილი</div>
          </div>
          <div style={{display:'flex',gap:8}}>

            <button onClick={handleDelete}
              style={{display:'flex',alignItems:'center',gap:5,padding:'7px 12px',background:'var(--danger-bg)',border:'1px solid #5a1a1a',borderRadius:8,fontSize:12,color:'var(--danger)',cursor:'pointer'}}>
              <Trash2 size={13} /> წაშლა
            </button>
          </div>
        </div>
        {fileType === 'pdf' && (
          <div style={{borderRadius:10,overflow:'hidden',border:'1px solid #1e2d45',height:600,position:'relative'}}>
            <iframe
              src={`/api/books/${bookId}/file?token=${localStorage.getItem('token')}`}
              width="100%"
              height="100%"
              style={{border:'none',background:'var(--bg-input)'}}
              title="PDF Viewer"
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.epub"
        style={{display:'none'}}
        onChange={e => { const f = e.target.files?.[0]; if(f) handleFile(f); }}
      />
      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if(f) handleFile(f); }}
        style={{border:`2px dashed ${dragOver?'var(--accent)':'var(--border)'}`,borderRadius:12,padding:40,textAlign:'center',transition:'all 0.2s',background:dragOver?'var(--accent-bg)':'transparent'}}>
        <Upload size={36} style={{color:dragOver?'var(--accent)':'var(--text-dimmed)',margin:'0 auto 12px',display:'block'}} />
        <p style={{fontSize:13,color:'var(--text-faint)',marginBottom:16}}>PDF ან EPUB ჩააგდე აქ</p>
        <button
          type="button"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          style={{display:'inline-flex',alignItems:'center',gap:6,padding:'9px 20px',background:'#1d4ed8',border:'none',borderRadius:8,color:'white',fontSize:13,fontWeight:500,cursor:'pointer',opacity:uploading?0.6:1}}>
          <Upload size={14} />
          {uploading ? 'იტვირთება...' : 'ფაილის არჩევა'}
        </button>
        <p style={{fontSize:11,color:'var(--text-dimmed)',marginTop:10}}>მაქსიმუმ 100MB · PDF, EPUB</p>
      </div>
    </div>
  );
}
